<template>
  <Modal
    :title="modalTitle"
    :saving="saving"
    @hide-modal="emit('close')"
    @submit="handleSave"
  >
    <div>
      <Stepper :items="stepperItems">
        <template #icon="{ item }">
          <svg
            v-if="item.completed"
            class="w-2.5 h-2.5"
            style="color: white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="3.5"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
          <span v-else class="text-[10px] font-bold" style="color: #9ca3af">{{
            item.index + 1
          }}</span>
        </template>

        <template #item="{ item }">
          <span
            v-if="firstErrorMessage(v$[item.fieldName])"
            class="step-error text-xs block mb-1"
          >
            {{ firstErrorMessage(v$[item.fieldName]) }}
          </span>

          <div v-if="item.locked" class="text-xs text-gray-400 italic">
            Not set
          </div>
          <div v-else class="flex items-center gap-3">
            <StepDateTimeInput
              :key="`${item.fieldName}-${clearCounter}`"
              :step="item.step"
              :default-date="
                appointment.arrival_window_start || appointment.scheduled_time
              "
              @update:modelValue="handleUpdateTime"
            />
            <button
              v-if="formState[item.fieldName]"
              type="button"
              class="step-clear"
              data-test="clear-time"
              @click="clearTime(item.fieldName)"
            >
              clear
            </button>
            <span
              v-if="item.durationLabel"
              class="ml-auto text-xs text-gray-400 whitespace-nowrap"
            >
              {{ item.durationLabel }}:
              <span class="font-medium">{{ item.duration }}</span>
            </span>
          </div>
        </template>
      </Stepper>

      <hr class="my-3 border-gray-200" />

      <div>
        <div class="flex items-center justify-between mb-1">
          <p
            class="text-xs font-semibold uppercase tracking-wide text-gray-600"
          >
            Crew Members
          </p>
          <p
            v-if="visibleCrewMembers.length"
            class="text-[11px] text-gray-400 italic"
          >
            Uncheck "Use Tech's Time" for individual times
          </p>
        </div>
        <div>
          <div class="flex flex-col">
            <CrewTimesRow
              v-for="member in visibleCrewMembers"
              :key="`${member.id}-${member.index}`"
              :crew-member="member"
              :appointment="appointment"
              @remove-crew-member="handleRemoveCrew"
              @change="handleCrewMemberUpdate"
            />
          </div>
          <EligibleCrewForm
            :fetch-link="appointment?.links?.crew_member_list"
            :crew-members="allCrewMembers"
            @add-crew="handleAddingMember"
          />
        </div>
      </div>

      <div class="mt-3">
        <TextArea
          v-model="formState.reason"
          message="Reason for adjusting times"
          class="compact-textarea"
          :validation="firstErrorMessage(v$.reason) ? 'error' : 'none'"
          :helper-text="firstErrorMessage(v$.reason)"
        />
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from "vue";
import { useStore } from "vuex";
import {
  formatDateWithTimezone,
  formatTimeWithTimezone,
  isFalsy,
  normalizeId
} from "@/lib/helpers";
import { useFeatureFlag } from "@/lib/useFeatureFlag";
import { firstErrorMessage } from "@/lib/vuelidate_helpers";

import Modal from "@/components/Modal.vue";
import Stepper from "@/components/Seranova/Stepper/Stepper.vue";
import TextArea from "@/components/Seranova/TextArea/TextArea.vue";
import CrewTimesRow from "@/Job/CrewTimesRow.vue";
import EligibleCrewForm from "@/Job/EligibleCrewForm.vue";
import { formatDuration } from "@/mixins";
import {
  AppointmentTechnicianEntry,
  AppointmentV2
} from "@/typings/appointment";
import { TimelineStep } from "@/typings/scheduler";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import StepDateTimeInput from "./StepDateTimeInput.vue";

interface CrewMemberEntry {
  id: number;
  index: number;
  full_name?: string;
  department?: Record<string, unknown>;
  use_crew_leader_times: boolean;
  crew_leader_id?: number | null;
  assigned_technician_profile_id?: number | null;
  assigned_to?: { technician_id?: number | null };
  accepted_time?: string | null;
  dispatch_time?: string | null;
  arrival_time?: string | null;
  departure_time?: string | null;
  [key: string]: unknown;
}

const props = defineProps<{
  appointment: AppointmentV2;
  appointmentTechnicianEntry?: AppointmentTechnicianEntry;
  technicianProfileId?: number | null;
}>();

const emit = defineEmits<{
  close: [];
  "refresh-appointment": [];
}>();

const store = useStore();
const saving = ref(false);
const clearCounter = ref(0);

const isMultiTechEnabled = useFeatureFlag("multiple_technicians_ff_enabled");

const preWorkEnabled = computed(() =>
  store.getters.settingEnabledForTenant("pre_work_enabled")
);

const normalizedTechId = computed(
  () => normalizeId(props.technicianProfileId) as number | null
);

const modalTitle = "Edit Times & Crew Members";

// --- Time fields ---

const TIME_ORDER = [
  "accepted_time",
  "dispatch_time",
  "arrival_time",
  "departure_time"
] as const;

const ALL_TIME_FIELDS = [
  { name: "accepted_time", label: "Claimed" },
  { name: "dispatch_time", label: "Travel Started" },
  { name: "arrival_time", label: "Arrived" },
  { name: "departure_time", label: "Completed" }
] as const;

const TIME_FIELDS = computed(() =>
  preWorkEnabled.value
    ? ALL_TIME_FIELDS
    : ALL_TIME_FIELDS.filter((f) => f.name !== "accepted_time")
);

const timeSource = computed(
  () => props.appointmentTechnicianEntry ?? props.appointment
);

const formState = reactive({
  accepted_time: timeSource.value.accepted_time ?? null,
  dispatch_time: timeSource.value.dispatch_time ?? null,
  arrival_time: timeSource.value.arrival_time ?? null,
  departure_time: timeSource.value.departure_time ?? null,
  reason: ""
});

// In single-tech mode, the backend rejects adding times that don't already
// exist (enforce_actual_times XOR check). Lock empty fields to prevent
// the user from attempting to add them. Multi-tech updates go through
// the AT-level path which has no such restriction.
const originalTimes: Record<string, boolean> = {
  accepted_time: !!timeSource.value.accepted_time,
  dispatch_time: !!timeSource.value.dispatch_time,
  arrival_time: !!timeSource.value.arrival_time,
  departure_time: !!timeSource.value.departure_time
};

const isFieldLocked = (fieldName: string): boolean =>
  !isMultiTechEnabled.value && !originalTimes[fieldName];

const FIELD_LABELS: Record<string, string> = {
  accepted_time: "Claimed",
  dispatch_time: "Travel Started",
  arrival_time: "Arrived",
  departure_time: "Completed"
};

const DURATION_CONFIG: Record<string, { field: string; label: string }> = {
  accepted_time: { field: "pre_work_minutes", label: "Pre-work" },
  dispatch_time: { field: "travel_minutes", label: "Drive Time" },
  arrival_time: { field: "work_minutes", label: "Work Duration" },
  departure_time: { field: "duration_minutes", label: "Total Duration" }
};

const formatSummary = (isoTime: string | null) => {
  if (!isoTime) return "";
  return `${formatTimeWithTimezone(isoTime)}, ${formatDateWithTimezone(
    isoTime
  )}`;
};

const clearTime = (fieldName: string) => {
  formState[fieldName] = null;
  clearCounter.value++;
};

const notFuture = helpers.withMessage(
  "Cannot be set in the future",
  (value: string | null) => {
    if (!value) return true;
    return new Date(value) <= new Date();
  }
);

const predecessorRule = (predecessors: string[]) =>
  helpers.withMessage(
    () => {
      const missing = predecessors
        .filter((p) => !formState[p])
        .map((p) => FIELD_LABELS[p]);
      return `Requires ${missing.join(", ")} to be set first`;
    },
    (value: string | null) => {
      if (!value) return true;
      return predecessors.every((p) => !!formState[p]);
    }
  );

const reasonRequired = helpers.withMessage("Value is required", required);

const rules = computed(() => {
  if (preWorkEnabled.value) {
    return {
      accepted_time: { notFuture, $autoDirty: true },
      dispatch_time: { notFuture, $autoDirty: true },
      arrival_time: {
        notFuture,
        predecessors: predecessorRule(["dispatch_time"]),
        $autoDirty: true
      },
      departure_time: {
        notFuture,
        predecessors: predecessorRule(["dispatch_time", "arrival_time"]),
        $autoDirty: true
      },
      reason: { reasonRequired, $autoDirty: true }
    };
  }
  return {
    dispatch_time: { notFuture, $autoDirty: true },
    arrival_time: {
      notFuture,
      predecessors: predecessorRule(["dispatch_time"]),
      $autoDirty: true
    },
    departure_time: {
      notFuture,
      predecessors: predecessorRule(["dispatch_time", "arrival_time"]),
      $autoDirty: true
    },
    reason: { reasonRequired, $autoDirty: true }
  };
});

const v$ = useVuelidate(rules, formState);

const stepperItems = computed(() =>
  TIME_FIELDS.value.map((field, index) => {
    const hasValue = !!formState[field.name];
    const timeVal = formState[field.name];
    const durationCfg = DURATION_CONFIG[field.name];
    const durationMinutes = (timeSource.value as Record<string, unknown>)[
      durationCfg.field
    ] as number | null | undefined;
    const locked = isFieldLocked(field.name);
    return {
      title: field.label,
      fieldName: field.name,
      index,
      locked,
      completed: hasValue,
      summary: formatSummary(timeVal),
      formattedTime: timeVal ? formatTimeWithTimezone(timeVal) : "",
      formattedDate: timeVal ? formatDateWithTimezone(timeVal) : "",
      durationLabel: hasValue ? durationCfg.label : "",
      duration:
        hasValue && !isFalsy(durationMinutes)
          ? formatDuration(durationMinutes as number)
          : "--",
      circleFill: hasValue ? "#16a34a" : "#f3f4f6",
      circleBorder: hasValue ? "#16a34a" : "#d1d5db",
      lineColor: hasValue ? "#86efac" : "#e5e7eb",
      step: new TimelineStep({
        title: field.label,
        editable: true,
        editableFieldName: field.name,
        time: timeVal ? formatTimeWithTimezone(timeVal) : "",
        date: timeVal ? formatDateWithTimezone(timeVal) : ""
      })
    };
  })
);

const handleUpdateTime = (data: Record<string, string>) => {
  const fieldName = Object.keys(data)[0];
  formState[fieldName] = data[fieldName];
};

// --- Crew members ---

const allCrewMembers = ref<CrewMemberEntry[]>(
  (props.appointment.crew_members || []).map(
    (member: CrewMemberEntry, i: number) => ({
      ...member,
      index: i
    })
  )
);

const nextIndex = ref(allCrewMembers.value.length);

const isCrewMemberForTech = (member: CrewMemberEntry): boolean => {
  if (!isMultiTechEnabled.value || normalizedTechId.value == null) return true;
  const assignedToId = normalizeId(member?.assigned_to?.technician_id);
  const crewLeaderId = normalizeId(member?.crew_leader_id);
  const apptScopedId = normalizeId(member?.assigned_technician_profile_id);
  return (
    apptScopedId === normalizedTechId.value ||
    assignedToId === normalizedTechId.value ||
    crewLeaderId === normalizedTechId.value
  );
};

const visibleCrewMembers = computed(() =>
  allCrewMembers.value.filter(isCrewMemberForTech)
);

const handleAddingMember = (member: CrewMemberEntry) => {
  const crewLeaderId = normalizedTechId.value ?? null;
  allCrewMembers.value = [
    ...allCrewMembers.value,
    {
      ...member,
      crew_leader_id: crewLeaderId,
      use_crew_leader_times: true,
      index: nextIndex.value
    }
  ];
  nextIndex.value++;
};

const handleRemoveCrew = (removeMember: CrewMemberEntry) => {
  allCrewMembers.value = allCrewMembers.value.filter(
    (member) =>
      !(member.index === removeMember.index && member.id === removeMember.id)
  );
};

const handleCrewMemberUpdate = (crewMember: CrewMemberEntry) => {
  allCrewMembers.value = allCrewMembers.value.map((member) => {
    if (member.index === crewMember.index && member.id === crewMember.id) {
      return { ...crewMember };
    }
    return member;
  });
};

// --- Crew payload builder ---

const crewTechProfileId = (crew: CrewMemberEntry): number | null => {
  return (
    (normalizeId(crew?.crew_leader_id) as number | null) ||
    (normalizeId(crew?.assigned_technician_profile_id) as number | null) ||
    (normalizeId(crew?.assigned_to?.technician_id) as number | null) ||
    null
  );
};

const hasCompleteOverrideTimes = (crew: CrewMemberEntry): boolean => {
  return Boolean(
    crew.dispatch_time && crew.arrival_time && crew.departure_time
  );
};

const buildCrewMembersPayload = (): {
  crewMembersPayload: Record<string, unknown>[];
  validationError: string | null;
} => {
  const crewMembersPayload: Record<string, unknown>[] = [];
  for (const crew of allCrewMembers.value) {
    let payload: Record<string, unknown> = {
      id: crew.id,
      assigned_technician_profile_id: crewTechProfileId(crew)
    };

    if (!crew.use_crew_leader_times) {
      if (!hasCompleteOverrideTimes(crew)) {
        if (isCrewMemberForTech(crew)) {
          return {
            crewMembersPayload: [],
            validationError:
              "You must set Dispatch, Arrival, and Departure for crew members with Use Tech's Time unchecked."
          };
        }
      } else {
        payload = {
          ...payload,
          accepted_time: crew.accepted_time,
          dispatch_time: crew.dispatch_time,
          arrival_time: crew.arrival_time,
          departure_time: crew.departure_time
        };
      }
    }

    crewMembersPayload.push(payload);
  }

  return { crewMembersPayload, validationError: null };
};

// --- Submit ---

const handleSave = async () => {
  const valid = await v$.value.$validate();
  if (!valid) return;

  const { crewMembersPayload, validationError } = buildCrewMembersPayload();
  if (validationError) {
    window.notify.error(validationError);
    return;
  }

  saving.value = true;
  try {
    const payload: Record<string, unknown> = {
      id: props.appointment.id,
      accepted_time: formState.accepted_time || null,
      dispatch_time: formState.dispatch_time || null,
      arrival_time: formState.arrival_time || null,
      departure_time: formState.departure_time || null,
      crew_members: crewMembersPayload,
      reason: formState.reason
    };

    if (normalizedTechId.value != null) {
      payload.technician_profile_id = normalizedTechId.value;
    }

    await store.dispatch("updateAppointmentTimes", payload);

    window.notify.success("Times and crew updated");
    emit("close");
    emit("refresh-appointment");
  } catch {
    // Backend errors are displayed automatically
  } finally {
    saving.value = false;
  }
};
</script>

<style scoped>
.step-error {
  color: #dc2626;
}

.step-clear {
  font-size: 0.6875rem;
  color: #9ca3af;
  background: none;
  border: none;
  padding: 0.125rem 0;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
  transition: color 0.1s ease;
  white-space: nowrap;
}

.step-clear:hover {
  color: #dc2626;
}

.compact-textarea :deep(textarea) {
  height: 56px;
  min-height: 56px;
}
</style>
